/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-loop-func */
import styled from '@emotion/styled'
import { ApolloError, useMutation } from '@graphcommerce/graphql'
import { Image } from '@graphcommerce/image'
import { CustomerDocument, useCustomerQuery } from '@graphcommerce/magento-customer'
import { i18n } from '@lingui/core'
import {
  Button,
  CircularProgress,
  LinearProgress,
  Modal,
  Rating,
  Switch,
  TextareaAutosize,
  Tooltip,
} from '@mui/material'
import axios from 'axios'
import PageLink from 'next/link'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import useCallAPI from '../../hooks/useCallAPI'
import { GenerateSignedCommandUrlDocument } from '../Artwork/GenerateSignedCommandUrl.gql'
import { StandardButton } from '../Button/ButtonStyles'
import { s3File } from '../FileUpload/FileUploadModal'
import { Icon } from '../Layout/Icon'
import { SlideinModalHeader } from '../Layout/SlideinModalHeader'
import { AlertPrompt } from '../Prompt/AlertPrompt'
import { ErrorPrompt } from '../Prompt/ErrorPrompt'
import { TextInput } from '../TextInput'
import { StandardTextField } from '../TextInput/TextFieldStyles'

type AddReviewModalProps = {
  productLink?: string
  productName?: string | null | undefined
  productId?: string
  thumbnail?: any
  isDisplayed: boolean
  setIsDisplayed: Dispatch<SetStateAction<boolean>>
  customer?: any
  orderId?: string | undefined
}

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 6,
  borderRadius: 5,
  backgroundColor: '#D1D3D4',
}))

export function AddReviewModal(props: AddReviewModalProps) {
  const {
    productLink,
    productName,
    productId,
    thumbnail,
    isDisplayed,
    setIsDisplayed,
    customer,
    orderId,
  } = props
  const [isDisplayAlertPrompt, setisDisplayAlertPrompt] = useState<boolean>(false)
  const [reviewMsg, setReviewMsg] = useState<string>()
  const [reviewTitle, setReviewTitle] = useState<string>()

  const [isAnonymous, setIsAnonymous] = useState<boolean>(false)

  const [rating, setRating] = useState<number | null>(5)
  const [hover, setHover] = useState(5)
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false)
  const [generateSignedCommandUrl] = useMutation(GenerateSignedCommandUrlDocument)
  const [localFiles, setLocalFiles] = useState<Array<s3File>>([])
  const inputFilesRef = useRef(null)

  const [apolloError, setApolloError] = useState<Error>()
  const [progress, setProgress] = useState<number>(0)

  const router = useRouter()

  const { error, apiCall } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/v1/widget/reviews`,
    contentType: 'application/json',
    method: 'POST',
  })

  const { error: synchApiError, apiCall: synchApiCall } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/reviews/dynamic_create`,
    contentType: 'application/json',
    method: 'POST',
  })

  const handleToggleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setIsAnonymous(event.target.checked)
  }

  const resetValues = () => {
    setisDisplayAlertPrompt(false)
    setRating(0)
    setReviewTitle(undefined)
    setReviewMsg(undefined)
    setIsAnonymous(false)
    setLocalFiles([])
  }

  const closeOnConfirm = () => {
    resetValues()
    setIsDisplayed(false)
    router.reload()
  }

  const handleClose = () => {
    resetValues()
    setIsDisplayed(false)
  }

  const sendPOST = async (body: any, url: string) => {
    const rawResponse = await fetch(url, {
      method: 'PUT',
      body,
    }).catch((e: Error) => {
      setApolloError(e)
      setIsUploadingFiles(false)
      setProgress(0)
    })

    await axios
      .request({
        headers: { 'Content-Type': 'multipart/form-data' },
        method: 'PUT',
        url,
        data: body,

        onUploadProgress: (p) => {
          const total = p?.total ?? p.bytes
          setProgress(Math.floor((p.loaded * 100) / total))
        },
      })
      .catch((e: Error) => {
        setApolloError(e)
        setIsUploadingFiles(false)
        setProgress(0)
      })
    setProgress(0)
    return url?.substring(0, rawResponse?.url?.indexOf('?'))
  }

  const handleSetReviewImgVidFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (localFiles?.length >= 3) {
      return
    }
    setProgress(0)
    setIsUploadingFiles(true)
    const localUploadedFiles = [...localFiles]
    if (event?.target?.files) {
      const s3FileName = `reviews/${Date.now()}-${event?.target?.files[0]?.name}`
      const signedUrl = await generateSignedCommandUrl({
        variables: { objectKey: s3FileName, bucket: 'artwork' },
      }).catch((e: ApolloError) => {
        setApolloError(e)
        setIsUploadingFiles(false)
        setProgress(0)
      })
      if (signedUrl) {
        const result = await sendPOST(
          event?.target?.files[0],
          signedUrl.data?.generateSignedCommandUrl?.url ?? '',
        )
        const newS3File = { url: result, fileName: event?.target?.files[0]?.name }
        localUploadedFiles.push(newS3File)
        setIsUploadingFiles(false)
        setLocalFiles(localUploadedFiles)
      }
    }
  }

  const handleAddReview = async () => {
    const multimediaUrlArry = localFiles.map(({ fileName, ...otherAttr }) => otherAttr.url)
    const body = {
      appkey: process.env.NEXT_PUBLIC_YOTPO_APP_KEY,
      domain: process.env.NEXT_PUBLIC_BASE_URL,
      sku: productId,
      product_title: productName,
      product_url: `${process.env.NEXT_PUBLIC_BASE_URL}${productLink}`,
      product_image_url: thumbnail.url,
      display_name: isAnonymous ? 'Anonymous' : `${customer?.firstname} ${customer?.lastname}`,
      email: customer?.email,
      review_content: reviewMsg,
      review_title: reviewTitle,
      review_score: rating,
      reviewer_type: 'verified_buyer',
      order_metadata: {
        order_id: orderId,
      },
      ...(multimediaUrlArry?.length > 0 && { image_urls: [...multimediaUrlArry] }),
    }

    const response =
      multimediaUrlArry && multimediaUrlArry.length > 0
        ? await synchApiCall(body)
        : await apiCall(body)
    if (response?.status === 200 || response?.status === 201) {
      setisDisplayAlertPrompt(true)
      const userSessionReviewedProducts = sessionStorage.getItem('userReviewedProducts')
      if (userSessionReviewedProducts && userSessionReviewedProducts.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const userSessionReviewedProductsArry = userSessionReviewedProducts.split(',')
        userSessionReviewedProductsArry.push(productId ?? '')
        sessionStorage.setItem('userReviewedProducts', userSessionReviewedProducts.toString())
      } else {
        sessionStorage.setItem('userReviewedProducts', productId ?? '')
      }
    }
  }

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event?.dataTransfer?.files) {
      const filesArray = Array.from(event?.dataTransfer?.files)
      const localUploadedFiles = [...localFiles]
      for (let idx = 0; idx < filesArray.length; idx++) {
        setIsUploadingFiles(true)
        const s3FileName = `artworks/${Date.now()}-${filesArray[idx].name}`
        // eslint-disable-next-line no-await-in-loop
        const signedUrl = await generateSignedCommandUrl({
          variables: { objectKey: s3FileName, bucket: 'artwork' },
        }).catch((e: ApolloError) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setApolloError(e)
          setIsUploadingFiles(false)
        })

        if (signedUrl) {
          // eslint-disable-next-line no-await-in-loop
          const result = await sendPOST(
            filesArray[idx],
            signedUrl.data?.generateSignedCommandUrl?.url ?? '',
          )
          const newS3File = { url: result, fileName: filesArray[idx].name }
          localUploadedFiles.push(newS3File)
          setLocalFiles(localUploadedFiles)
          if (idx === filesArray.length - 1) {
            setIsUploadingFiles(false)
          }
        }
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleRemoveItem = (index: number) => {
    const localFilesCopy = [...localFiles]
    setLocalFiles(localFilesCopy.filter((item, idx) => idx !== index))
  }

  const labels: { [index: string]: string } = {
    1: 'Terrible',
    2: 'Poor',
    3: 'Average',
    4: 'Good',
    5: 'Amazing',
  }

  const getLabelText = (value: number) => `${value} Star${value !== 1 ? 's' : ''}, ${labels[value]}`

  return (
    <Modal className='z-10 ' open={isDisplayed} onClose={handleClose}>
      <div className='absolute left-[10vw] top-[5vh] w-[80vw] overflow-auto rounded-md border-solid bg-pure-white md:left-[calc(50vw-200px)] md:w-[400px]'>
        <div>
          <SlideinModalHeader
            title={i18n._('Leave a Review')}
            onClose={handleClose}
            withHorizontalMargin
          />
        </div>
        <div className='flex max-h-[680px] flex-col items-center justify-start bg-pure-white lg:h-auto'>
          <div className='mx-5 flex w-[90%] flex-col items-center justify-center gap-[12px] lg:mx-0 lg:gap-[12px]'>
            <div className='grid w-full grid-cols-1 items-center justify-center gap-[12px] lg:gap-[40px]'>
              <div className='flex flex-col gap-[12px]'>
                <PageLink
                  href={productLink ?? '/'}
                  passHref
                  className='Type-XL-Bold text-tight-black'
                >
                  {productName}
                </PageLink>
              </div>
            </div>
            <div className='grid w-full grid-cols-1 items-center justify-center gap-[8px] lg:gap-[40px]'>
              <div className='flex items-center justify-start'>
                <Rating
                  name='size-small'
                  defaultValue={0}
                  size='large'
                  precision={1}
                  className='align-center'
                  getLabelText={getLabelText}
                  onChange={(event, newValue) => {
                    setRating(newValue)
                  }}
                  onChangeActive={(event, newHover) => {
                    setHover(newHover)
                  }}
                />
              </div>
            </div>

            <div className='flex w-full grid-cols-1 flex-col items-center justify-center gap-[8px] lg:grid lg:gap-[40px]'>
              <StandardTextField
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setReviewTitle(e.target.value)
                }
                label='Title'
                value={reviewTitle}
                className='w-full'
                size='small'
              />
            </div>
            <div className='flex w-full grid-cols-1 flex-col items-center justify-center gap-[8px] lg:grid lg:gap-[40px]'>
              <TextareaAutosize
                placeholder='Type your review here...'
                value={reviewMsg}
                minRows={3}
                onChange={(e) => setReviewMsg(e.target.value)}
                className='w-full min-w-full rounded-[4px] border-black/30 bg-pure-white text-[14px] hover:border-black focus:border-material-ui-blue'
              />
            </div>
            <div className='flex w-full grid-cols-1 flex-col items-center justify-center gap-[8px] lg:grid lg:gap-[40px]'>
              <div className='flex w-full items-center justify-start'>
                <span className='Type-Large-Regular text-black'>
                  {i18n._('Leave review as anonymous:')}
                </span>
                <Switch
                  checked={isAnonymous}
                  onChange={handleToggleSwitch}
                  size='medium'
                  className='ml-[10px]'
                />
              </div>
            </div>
            <div className='flex w-full grid-cols-1 flex-col items-center justify-center gap-[8px] lg:grid lg:gap-x-[40px]'>
              <div
                className='flex w-full flex-col items-center justify-center'
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {localFiles?.length <= 2 && (
                  <label
                    className={`flex h-[140px] w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-material-ui-blue px-4 transition focus:outline-none ${
                      isUploadingFiles
                        ? 'bg-light-gray hover:border-medium-gray'
                        : ' bg-material-ui-blue/10 hover:border-material-ui-dark-blue'
                    }`}
                  >
                    <div
                      className={`${
                        isUploadingFiles ? 'hidden' : 'flex flex-col items-center justify-center'
                      }`}
                    >
                      <Icon
                        name='upload_file'
                        className='align-middle text-3xl text-material-ui-blue'
                      />
                      <span className='Type-Large-Regular mt-2 flex items-center space-x-2'>
                        <span className='text-gray-600 text-center font-medium'>
                          <span className='text-material-ui-blue underline'>
                            {i18n._('Click to upload')}
                          </span>
                          {i18n._(' or drag and drop your images here.')}
                        </span>
                      </span>
                      <span className='Type-Base-Regular mt-5 text-70-grey'>
                        {i18n._('JPG or PNG (max. 20MB)')}
                      </span>
                      {localFiles?.length <= 2 && (
                        <span className='Type-Base-Regular mt-1 text-70-grey'>
                          {i18n._('Maximum of 3 files')}
                        </span>
                      )}
                    </div>
                    <div
                      className={`${
                        isUploadingFiles ? 'flex items-center justify-center' : 'hidden'
                      }`}
                    >
                      <span>Uploading files. Please wait.</span>
                    </div>
                    <input
                      type='file'
                      name='file_upload'
                      className='hidden'
                      ref={inputFilesRef}
                      onChange={handleSetReviewImgVidFiles}
                      onDrop={handleDrop}
                      disabled={isUploadingFiles}
                    />
                  </label>
                )}

                <div className='mt-[4px] flex flex-col items-center justify-center text-material-ui-blue'>
                  <div className='mt-2 h-[10px]'>
                    {isUploadingFiles && (
                      <BorderLinearProgress
                        className='w-[50vw] max-w-[300px] lg:w-[300px]'
                        variant='determinate'
                        value={progress}
                        color='inherit'
                      />
                    )}
                  </div>
                  <div className='mt-2 flex flex-row items-start justify-center'>
                    {localFiles.length > 0 &&
                      localFiles?.map((item, index) => (
                        <div
                          key={item.url}
                          className='flex flex-col items-center justify-center px-2 '
                        >
                          <img src={item.url} alt={item.fileName} className='h-[50px] w-[50px]' />
                          <Icon
                            name='disabled_by_default'
                            className={`cursor-pointer align-middle text-xl lg:text-3xl ${
                              isUploadingFiles
                                ? 'text-light-gray hover:text-medium-gray'
                                : 'text-material-ui-blue hover:text-material-ui-dark-blue'
                            }`}
                            onClick={() => {
                              if (!isUploadingFiles) {
                                handleRemoveItem(index)
                              }
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div />
              <div className='mb-4 flex w-full flex-col items-center justify-center gap-4 md:flex-row'>
                <Button
                  type='button'
                  variant='outlined'
                  color='primary'
                  size='small'
                  className='w-full bg-pure-white uppercase md:w-auto'
                  onClick={() => {
                    handleClose()
                  }}
                >
                  {i18n._('Cancel')}
                </Button>
                <StandardButton
                  type='button'
                  variant='contained'
                  color='primary'
                  size='small'
                  className='w-full bg-material-ui-blue uppercase md:w-auto'
                  onClick={() => handleAddReview()}
                  disabled={!reviewTitle || !reviewMsg}
                >
                  {i18n._('Submit Review')}
                </StandardButton>
              </div>
            </div>
            <ErrorPrompt error={error ?? apolloError ?? synchApiError} />
            <AlertPrompt
              title={i18n._('Thank you')}
              isDisplayed={isDisplayAlertPrompt}
              setIsDisplayed={setisDisplayAlertPrompt}
              message={i18n._('Thank you for leaving a review!')}
              callback={closeOnConfirm}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
